import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { respondToInvitation } from '../../services/calendarInvitesService';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * Accept / Decline for an `appointment_invitation` bell notification — ONE
 * component shared by the NotificationIcon dropdown and NotificationsModal
 * (the team-invitation button pair's shape, PLAN §3.4). Reads
 * `data.invitation_token`, responds through THE single service path, and
 * reports the new state up so the caller can patch its notification list the
 * way handleInvite does for teams. `data.invitation_status` (injected by the
 * backend's fetch_invitation_statuses join) renders historical entries in
 * their live state.
 */
export default function AppointmentInviteActions({ notification, isDark = false, onResponded }) {
  const [busy, setBusy] = useState(false);
  const status = notification?.data?.invitation_status;
  const token = notification?.data?.invitation_token;

  if (status === 'accepted') {
    return (
      <div className="flex items-center gap-1.5 mt-2">
        <Check size={12} style={{ color: '#16a34a' }} />
        <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>Added to calendar</span>
      </div>
    );
  }
  if (status === 'declined') {
    return (
      <div className="flex items-center gap-1.5 mt-2">
        <span style={{ fontSize: 11, color: isDark ? '#71717a' : '#9ca3af', fontWeight: 500 }}>
          Invitation declined
        </span>
      </div>
    );
  }
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-1.5 mt-2">
        <span style={{ fontSize: 11, color: isDark ? '#71717a' : '#9ca3af', fontWeight: 500 }}>
          Cancelled by the organizer
        </span>
      </div>
    );
  }
  if (!token) return null;

  const respond = async (action) => {
    if (busy) return;
    setBusy(true);
    const { success, info, error } = useNotificationStore.getState();
    try {
      // The notification's own data doubles as the invitation row (snapshot +
      // appointment_uid ride in it) so accept can materialize immediately
      // even before any calendar surface loaded the pending list.
      await respondToInvitation(token, action, notification?.data || null);
      if (action === 'accept') success('Added to your calendar');
      else info('Invitation declined');
      onResponded?.(notification, action === 'accept' ? 'accepted' : 'declined');
    } catch (err) {
      error(err?.response?.data?.error || err.message || 'Failed to respond to invitation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <button
        onClick={() => respond('decline')}
        disabled={busy}
        style={{
          padding: '3px 12px',
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 6,
          cursor: 'pointer',
          border: `1px solid ${isDark ? '#52525b' : '#e5e7eb'}`,
          background: isDark ? '#27272a' : '#f3f4f6',
          color: isDark ? '#a1a1aa' : '#4b5563',
        }}
      >
        Decline
      </button>
      <button
        onClick={() => respond('accept')}
        disabled={busy}
        className="px-3 py-1 text-xs font-semibold rounded-md transition-colors"
        style={{ background: 'var(--accent)', color: 'var(--accent-fg, #fff)' }}
      >
        Accept
      </button>
    </div>
  );
}
